export function findPreviousContentCoordinates(coordList, targetCoords) {
	const { x1: tx1, y1: ty1, y2: ty2 } = targetCoords;
	const above = coordList
		.filter((item) => item.y2 <= ty1)
		.sort((a, b) => {
			const dy = b.y2 - a.y2;
			if (dy !== 0) {
				return dy;
			}
			return a.x1 - b.x1;
		});
	if (above.length > 0) {
		return above[0];
	}
	const verticallyOverlapping = (item) => !(item.y2 < ty1 || item.y1 > ty2);
	const left = coordList
		.filter((item) => verticallyOverlapping(item) && item.x2 <= tx1)
		.sort((a, b) => {
			const dx = b.x2 - a.x2;
			if (dx !== 0) {
				return dx;
			}
			return a.y1 - b.y1;
		});
	if (left.length > 0) {
		return left[0];
	}
	return null;
}

export function doesRectOverlap(
	rectRef,
	selector,
	mode = "any" // "any" | "multiple"
) {
	if (!rectRef.current) {
		return false;
	}
	const rectA = rectRef.current.getBoundingClientRect();
	const elements = document.querySelectorAll(selector);
	let count = 0;
	for (const el of elements) {
		if (el === rectRef.current) {
			continue;
		}
		const rectB = el.getBoundingClientRect();
		if (
			rectA.left < rectB.right &&
			rectA.right > rectB.left &&
			rectA.top < rectB.bottom &&
			rectA.bottom > rectB.top
		) {
			count++;
			if (mode === "any") {
				return true;
			}
			if (mode === "multiple" && count > 1) {
				return true;
			}
		}
	}
	return false;
}

export const getSelectionCoordinates = ({ zoom, selectionRectangleRef, pageDimensions }) => {
	const zoomScale = zoom / 100;
	const viewer = document.querySelector("[class^='PdfViewer_pdfViewerList__container__']");
	if (!viewer) {
		return null;
	}
	const pages = Array.from(viewer.querySelectorAll("img[class^='PdfViewer_image__']"));
	const rectEl = selectionRectangleRef.current;
	const rectBox = rectEl.getBoundingClientRect();
	const viewerBox = viewer.getBoundingClientRect();
	const rect = {
		x1: rectBox.left - viewerBox.left,
		y1: rectBox.top - viewerBox.top,
		x2: rectBox.right - viewerBox.left,
		y2: rectBox.bottom - viewerBox.top,
	};
	let best = null;
	pages.forEach((img) => {
		const alt = img.alt || "";
		const match = alt.match(/image_(\d+)/);
		if (!match) {
			return;
		}
		const renderedPageIndex = Number(match[1]);
		const dims = pageDimensions.find((content) => content.page === renderedPageIndex + 1);
		if (!dims) {
			return;
		}
		const pageBox = img.getBoundingClientRect();
		const p = {
			x1: pageBox.left - viewerBox.left,
			y1: pageBox.top - viewerBox.top,
			x2: pageBox.right - viewerBox.left,
			y2: pageBox.bottom - viewerBox.top,
		};
		const ix1 = Math.max(rect.x1, p.x1);
		const iy1 = Math.max(rect.y1, p.y1);
		const ix2 = Math.min(rect.x2, p.x2);
		const iy2 = Math.min(rect.y2, p.y2);
		if (ix2 <= ix1 || iy2 <= iy1) {
			return;
		}
		const area = (ix2 - ix1) * (iy2 - iy1);
		const localRendered = {
			x1: ix1 - p.x1,
			y1: iy1 - p.y1,
			x2: ix2 - p.x1,
			y2: iy2 - p.y1,
		};
		const intrinsic = {
			x1: Math.round(localRendered.x1 / zoomScale),
			y1: Math.round(localRendered.y1 / zoomScale),
			x2: Math.round(localRendered.x2 / zoomScale),
			y2: Math.round(localRendered.y2 / zoomScale),
		};
		const result = {
			page: renderedPageIndex + 1,
			area,
			...intrinsic,
		};
		if (!best || result.area > best.area) {
			best = result;
		}
	});
	return best;
};

export function cropImageFromOverlap(imgAlt, rectRef) {
	const img = document.querySelector(`img[alt="${imgAlt}"]`);
	const rectEl = rectRef.current;
	if (!img || !rectEl) {
		return null;
	}
	const imgRect = img.getBoundingClientRect();
	const selRect = rectEl.getBoundingClientRect();
	const left = Math.max(imgRect.left, selRect.left);
	const top = Math.max(imgRect.top, selRect.top);
	const right = Math.min(imgRect.right, selRect.right);
	const bottom = Math.min(imgRect.bottom, selRect.bottom);

	if (right <= left || bottom <= top) {
		return null;
	}
	const scaleX = img.naturalWidth / imgRect.width;
	const scaleY = img.naturalHeight / imgRect.height;
	const cropX = (left - imgRect.left) * scaleX;
	const cropY = (top - imgRect.top) * scaleY;
	const cropW = (right - left) * scaleX;
	const cropH = (bottom - top) * scaleY;
	const canvas = document.createElement("canvas");
	canvas.width = cropW;
	canvas.height = cropH;
	canvas.getContext("2d").drawImage(img, cropX, cropY, cropW, cropH, 0, 0, cropW, cropH);
	return canvas.toDataURL("image/png");
}
